import { Dimensions } from 'react-native';

// the design width (portrait) for mobile phone
const kBaseWidth = 428;
const kBaseHeight = 926;

// https://28b.co.uk/wp-content/uploads/2021/02/ios-device-dimensions-21.htm
// iPad: 1024 × 768
// iPad 10.2″: 1080 × 810
// iPad 2: 1024 × 768
// iPad 3: 1024 × 768
// iPad 4: 1024 × 768
// iPad 5: 1024 × 768
// iPad 6: 1024 × 768
// iPad 7: 1024 × 768
// iPad Air: 1024 × 768
// iPad Air 2: 1024 × 768
// iPad Air 3: 1112 × 834
// iPad Air 4: 1180 × 820
// iPad Mini: 1024 × 768
// iPad Mini 2: 1024 × 768
// iPad Mini 3: 1024 × 768
// iPad Mini 4: 1024 × 768
// iPad Mini 5: 1024 × 768
// iPad Mini 6: 1133 × 744
// iPad Pro 9.7″: 1024 × 768
// iPad Pro 10.5″: 1112 × 834
// iPad Pro 11″: 1194 × 834
// iPad Pro 12.9″: 1366 × 1024

// the design width (landscape) for ipad
const kBaseWidthIpad = 1239; // (1366 + 1112) / 2

const kScreenWidth = Dimensions.get('window').width;
const kScreenHeight = Dimensions.get('window').height;

const kMinScaleFactor = 0.8;

type Options = {
  factor?: number;
  round?: boolean | 'round' | 'ceil' | 'floor';
  ignoreHeight?: boolean;
};

export type PixelScaleFn = (value: number, options?: Options) => number;

export const spxFactory =
  (targetWidth: number = kScreenWidth, targetHeight: number = kScreenHeight) =>
  (value: number, options?: Options) => {
    const factor = Math.max(
      kMinScaleFactor,
      Math.min(
        options?.factor ?? targetWidth / kBaseWidth,
        options?.factor ?? targetHeight / kBaseHeight,
      ),
    );
    const px = value * factor;
    if (!options?.round) {
      return px;
    }
    return options.round === true || options.round === 'round'
      ? Math.round(px)
      : options.round === 'ceil'
      ? Math.ceil(px)
      : Math.floor(px);
  };

/**
 * Converts a pixel value in base design to be scaled appropriately in target
 * device w/ smaller or larger screen dimensions.
 * @description default pixel scaler function
 * @param value the pixel value in base design
 * @param [factor] the optional custom scale factor
 * @returns converted pixel value
 */
export const spx: PixelScaleFn = spxFactory();

export const spxIdentity: PixelScaleFn = (value: number) => value;

export const spxForTablet: PixelScaleFn = value => {
  const width = Math.max(kScreenWidth, kScreenHeight);
  return (width / kBaseWidthIpad) * value;
};
